import {
  ClientExceptionEntityTypeEnum,
  ClientOrganizationProfileRoleEnum
} from '@DigitaleDoerfer/digitale-doerfer-api';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View } from 'react-native';
import { Button, RadioButton, Text } from 'react-native-paper';
import { object, string, z } from 'zod';

import { getClientErrorType } from '@/features/api/hooks/errorMiddleware';
import { useAddOrganizationMember } from '@/features/api/hooks/grapevine-organization/useAddOrganizationMember';
import { useGrapevineOrganizationApi } from '@/features/api/hooks/grapevine-organization/useGrapevineOrganizationApi';
import { BottomModal } from '@/features/popup/components/BottomModal';
import { usePopup } from '@/features/popup/provider/usePopup';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { FormTextInput } from '@/shared/components/forms/FormTextInput';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

type AddMemberRole = ClientOrganizationProfileRoleEnum;

type AddMemberForm = {
  email: string;
  role: AddMemberRole;
};

type Props = {
  organizationId: string;
  isVisible: boolean;
  onClose: () => void;
};

const EMAIL_CHECK_DEBOUNCE_MS = 600;

export const AddMemberModal: FC<Props> = ({
  organizationId,
  isVisible,
  onClose
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.organization-members.add'
  });
  const { t: tRoles } = useTranslation(LocalizationNameSpace.ORGANIZATION, {
    keyPrefix: 'organization.roleLabels'
  });
  const { showAlert } = usePopup();
  const api = useGrapevineOrganizationApi();

  // oxlint-disable-next-line unicorn/no-useless-undefined
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const debouncedEmailCheck = useCallback(
    (email: string): Promise<boolean> => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      return new Promise<boolean>((resolve) => {
        timerRef.current = setTimeout(async () => {
          try {
            const result = await api.checkAdminEmail({
              clientOrganizationAdminEmailCheckRequest: { adminEmail: email }
            });
            resolve(result._exists ?? false);
          } catch {
            resolve(false);
          }
        }, EMAIL_CHECK_DEBOUNCE_MS);
      });
    },
    [api]
  );

  const schema = useMemo(
    () =>
      object({
        email: string().superRefine(
          // oxlint-disable-next-line react/refs -- ref is only accessed inside async setTimeout callback, not during render
          async (value, ctx) => {
            const trimmed = value.trim();
            if (trimmed.length === 0) {
              ctx.addIssue({
                code: 'custom',
                message: t('errors.emailRequired')
              });
              return;
            }
            if (!z.string().email().safeParse(trimmed).success) {
              ctx.addIssue({
                code: 'custom',
                message: t('errors.emailInvalid')
              });
              return;
            }
            const valid = await debouncedEmailCheck(trimmed);
            if (!valid) {
              ctx.addIssue({
                code: 'custom',
                message: t('errors.emailNotFound')
              });
            }
          }
        ),
        role: z.enum([
          ClientOrganizationProfileRoleEnum.Admin,
          ClientOrganizationProfileRoleEnum.Funker
        ])
      }),
    [debouncedEmailCheck, t]
  );

  const {
    control,
    handleSubmit,
    setError,
    reset,
    formState: { isSubmitting }
  } = useForm<AddMemberForm>({
    resolver: zodResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: { email: '', role: ClientOrganizationProfileRoleEnum.Admin }
  });

  const { mutate: addMember, isPending } = useAddOrganizationMember();

  useEffect(() => {
    if (!isVisible) {
      reset({ email: '', role: ClientOrganizationProfileRoleEnum.Admin });
    }
  }, [isVisible, reset]);

  const onSubmit = useCallback(
    (data: AddMemberForm) => {
      addMember(
        {
          organizationId,
          email: data.email.trim(),
          roleKey: data.role
        },
        {
          onSuccess: () => onClose(),
          onError: (error) => {
            const type = getClientErrorType(error);
            let fieldMessage: string | undefined;
            switch (type as string | undefined) {
              case ClientExceptionEntityTypeEnum.PersonWithEmailNotFound: {
                fieldMessage = t('errors.emailNotFound');
                break;
              }
              case ClientExceptionEntityTypeEnum.RoleAlreadyAssigned: {
                fieldMessage = t('errors.roleAlreadyAssigned');
                break;
              }
            }
            if (fieldMessage) {
              setError('email', { type: 'server', message: fieldMessage });
              return;
            }
            showAlert({
              dismissable: true,
              labels: {
                title: t('errors.generic'),
                content: '',
                confirm: 'OK'
              }
            });
          }
        }
      );
    },
    [addMember, onClose, organizationId, setError, showAlert, t]
  );

  const canSubmit = !isSubmitting && !isPending;

  return (
    <BottomModal
      isVisible={isVisible}
      title={t('title')}
      titleAlign="center"
      onClose={onClose}
    >
      <View style={styles.body}>
        <Text variant="bodyLarge" style={styles.description}>
          {t('description')}
        </Text>

        <FormTextInput
          control={control}
          name="email"
          accessibilityLabel="Email input field"
          accessibilityHint={t('emailLabel')}
          mode="outlined"
          label={t('emailLabel')}
          keyboardType="email-address"
          autoCapitalize="none"
          autoComplete="email"
        />
        <Text variant="bodySmall" style={styles.emailHint}>
          <Trans
            i18nKey="profile-setting:screens.organization-members.add.emailHint"
            components={{ bold: <Text style={styles.bold}>{''}</Text> }}
          />
        </Text>

        <View style={styles.roleSection}>
          <Text variant="titleLarge" style={styles.roleHeader}>
            {t('roleSection')}
          </Text>
          <Controller
            control={control}
            name="role"
            render={({ field: { value, onChange } }) => (
              <RadioButton.Group
                value={value}
                onValueChange={(v) => onChange(v)}
              >
                <RoleOption
                  value={ClientOrganizationProfileRoleEnum.Admin}
                  title={tRoles('ADMIN')}
                  description={t('roleAdminDescription')}
                  selected={value === ClientOrganizationProfileRoleEnum.Admin}
                  onSelect={onChange}
                />
                <RoleOption
                  value={ClientOrganizationProfileRoleEnum.Funker}
                  title={tRoles('FUNKER')}
                  description={t('roleFunkerDescription')}
                  selected={value === ClientOrganizationProfileRoleEnum.Funker}
                  onSelect={onChange}
                />
              </RadioButton.Group>
            )}
          />
        </View>

        <Button
          mode="contained"
          onPress={handleSubmit(onSubmit)}
          disabled={!canSubmit}
          loading={isSubmitting || isPending}
          style={styles.cta}
        >
          {t('cta')}
        </Button>
      </View>
    </BottomModal>
  );
};

type RoleOptionProps = {
  value: AddMemberRole;
  title: string;
  description: string;
  selected: boolean;
  onSelect: (value: AddMemberRole) => void;
};

const RoleOption: FC<RoleOptionProps> = ({
  value,
  title,
  description,
  selected,
  onSelect
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  return (
    <Pressable
      accessibilityRole="button"
      onPress={() => onSelect(value)}
      style={styles.roleRow}
    >
      <RadioButton value={value} status={selected ? 'checked' : 'unchecked'} />
      <View style={styles.roleText}>
        <Text variant="titleMedium" style={styles.roleTitle}>
          {title}
        </Text>
        <Text variant="bodyMedium" style={styles.roleDescription}>
          {description}
        </Text>
      </View>
    </Pressable>
  );
};

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    body: {
      paddingHorizontal: 24,
      paddingBottom: 24,
      gap: 24
    },
    description: {
      color: theme.colors.onSurfaceVariant,
      textAlign: 'center'
    },
    emailHint: {
      color: theme.colors.onSurfaceVariant,
      textAlign: 'center'
    },
    bold: { fontWeight: 'bold' },
    roleSection: { gap: 8 },
    roleHeader: { color: theme.colors.onSurface },
    roleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 8,
      gap: 8
    },
    roleText: { flex: 1, gap: 2 },
    roleTitle: { color: theme.colors.onSurface },
    roleDescription: { color: theme.colors.onSurfaceVariant },
    cta: { marginTop: 8 }
  });
